import React, { useState, useEffect } from 'react';
import { FaTimes, FaUndo, FaSave, FaEdit } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/backendConfig';
import { enqueueAndPoll } from '../utils/jobPolling';
import { checkContentModeration, getContentModerationMessage } from '../utils/contentModeration';
import ContentModerationModal from './ContentModerationModal';

export default function SceneEditModal({ 
  isOpen, 
  onClose, 
  scene, 
  onSceneUpdate,
  onUndo // Add undo prop
}) {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [editHistory, setEditHistory] = useState([]);
  const [currentEditIndex, setCurrentEditIndex] = useState(-1);
  const [sceneData, setSceneData] = useState(null);
  const [showContentModerationModal, setShowContentModerationModal] = useState(false);
  const [contentModerationMessage, setContentModerationMessage] = useState('');
  const [contentModerationKeywords, setContentModerationKeywords] = useState([]);

  useEffect(() => {
    if (isOpen && scene) {
      setSceneData(scene);
      // Try multiple possible image URL properties
      const imageUrl = scene.imageUrl || scene.refinedImageUrl || scene.sceneImageUrl;
      console.log('SceneEditModal - Scene data:', scene);
      console.log('SceneEditModal - Image URL found:', imageUrl);
      setOriginalImageUrl(imageUrl);
      setCurrentImageUrl(imageUrl);
      setPrompt('');
      setEditHistory([]);
      setCurrentEditIndex(-1);
    }
  }, [isOpen, scene]);


  const handleEditScene = async () => {
    if (!prompt.trim()) {
      alert('Please enter a prompt describing the changes you want to make to the scene');
      return;
    }

    // Content moderation check
    const moderationCheck = checkContentModeration(prompt);
    if (moderationCheck.isInappropriate) {
      const errorMessage = getContentModerationMessage('scene edit', moderationCheck.matchedKeywords);
      setContentModerationMessage(errorMessage);
      setContentModerationKeywords(moderationCheck.matchedKeywords);
      setShowContentModerationModal(true);
      return;
    }

    setIsLoading(true);
    try {
      // Construct a preservation prompt (backend uses BFL Flux Kontext Pro for editing)
      const editPrompt = `Do not change the face appearance, height of the characters in the image, ensure proportions are retained along with the exact scene setting, only change: ${prompt}. Comforting Old french children book illustration style, high quality, detailed, no watermark, no signature, no text, no writing, no logo, no name in bottom right corner`;
      
      console.log('SceneEditModal - User prompt:', prompt);
      console.log('SceneEditModal - Complete edit prompt:', editPrompt);
      
      // Use enqueueAndPoll utility (same approach as ReimagineStudio)
      console.log('SceneEditModal - Using enqueueAndPoll for background worker processing');
      
      const data = await enqueueAndPoll(
        API_ENDPOINTS.editSceneImage,
        {
          imageUrl: currentImageUrl,
          prompt: editPrompt, // Send the complete edit prompt
          userPrompt: prompt, // Also send the original user prompt for reference
          sceneData: sceneData
        },
        {
          interval: 2000, // Poll every 2 seconds (same as ReimagineStudio)
          maxAttempts: 150, // 5 minutes max (same as ReimagineStudio)
          onProgress: (status, job, attempts) => {
            console.log(`SceneEditModal - Polling attempt ${attempts}:`, status.status);
            setIsLoading(true);
          },
          onStatusChange: (status) => {
            console.log('SceneEditModal - Job status changed:', status.status);
          },
          baseUrl: API_ENDPOINTS.backendBase
        }
      );

      console.log('SceneEditModal - Edit completed, response data:', data);
      console.log('SceneEditModal - Response data keys:', Object.keys(data || {}));
      
      // Extract image URL from response (same structure as ReimagineStudio)
      // enqueueAndPoll returns status.result || status.data || status
      // editSceneWorker returns { success: true, imageUrl: ..., ... }
      let newImageUrl = data.imageUrl || data.generatedImageUrl;
      
      if (!newImageUrl) {
        console.error('SceneEditModal - No imageUrl in response:', data);
        throw new Error('No image URL in response from edit worker');
      }
      
      // Convert relative URL to full URL if needed
      if (newImageUrl.startsWith('/uploads/')) {
        newImageUrl = `${API_ENDPOINTS.backendBase}${newImageUrl}`;
      }
      
      console.log('SceneEditModal - Edit completed, new image URL:', newImageUrl);
      
      // Add to edit history
      const newEdit = {
        imageUrl: newImageUrl,
        prompt: prompt,
        editPrompt: editPrompt,
        timestamp: new Date().toISOString()
      };
      
      setEditHistory(prev => [...prev, newEdit]);
      setCurrentEditIndex(editHistory.length);
      setCurrentImageUrl(newImageUrl);
      setPrompt('');
      setIsLoading(false);

    } catch (error) {
      console.error('Error editing scene image:', error);
      
      // Handle content moderation errors - check if error object has moderation data
      if (error.error === 'Content moderation violation' || 
          (error.message && (error.message.includes('Content moderation') || error.message.includes('Norms defined by Artmosphere')))) {
        const errorMessage = error.message || getContentModerationMessage('scene edit', error.matchedKeywords || []);
        setContentModerationMessage(errorMessage);
        setContentModerationKeywords(error.matchedKeywords || []);
        setShowContentModerationModal(true);
      } else {
        alert(error.message || 'Failed to edit scene image. Please try again.');
      }
      
      setIsLoading(false);
    }
  };

  const handleUndo = () => {
    if (currentEditIndex > 0) {
      const newIndex = currentEditIndex - 1;
      setCurrentEditIndex(newIndex);
      setCurrentImageUrl(editHistory[newIndex].imageUrl);
    } else if (currentEditIndex === 0) {
      setCurrentEditIndex(-1);
      setCurrentImageUrl(originalImageUrl);
    }
  };

  const handleRedo = () => {
    if (currentEditIndex < editHistory.length - 1) {
      const newIndex = currentEditIndex + 1;
      setCurrentEditIndex(newIndex);
      setCurrentImageUrl(editHistory[newIndex].imageUrl);
    }
  };

  const handleSave = async () => {
    try {
      // Update scene with new image URL
      const updatedScene = {
        ...sceneData,
        imageUrl: currentImageUrl,
        // Preserve all other properties
        sceneNumber: sceneData.sceneNumber,
        description: sceneData.description,
        characterDetails: sceneData.characterDetails,
        tags: sceneData.tags,
        genre: sceneData.genre,
        mood: sceneData.mood,
        medium: sceneData.medium,
        createdAt: sceneData.createdAt
      };

      console.log('SceneEditModal - Saving updated scene:', updatedScene);

      // Call parent callback
      if (onSceneUpdate) {
        onSceneUpdate(updatedScene);
      }

      // Small delay to ensure state updates are processed
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error) {
      console.error('Error saving scene:', error);
      alert('Failed to save scene changes. Please try again.');
    }
  };

  const handleCancel = () => {
    setCurrentImageUrl(originalImageUrl);
    setPrompt('');
    setEditHistory([]);
    setCurrentEditIndex(-1);
    onClose();
  };

  // Construct full image URL if needed
  const fullImageUrl = currentImageUrl && currentImageUrl.startsWith('/uploads/') 
    ? `${API_ENDPOINTS.backendBase}${currentImageUrl}` 
    : currentImageUrl || 'https://dummyimage.com/400x400/eee/aaa&text=No+Image';

  // Debug logging
  console.log('SceneEditModal - currentImageUrl:', currentImageUrl);
  console.log('SceneEditModal - fullImageUrl:', fullImageUrl);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-amber-800 flex items-center gap-2">
            <FaEdit /> Edit Scene
          </h2>
          <div className="flex items-center gap-2">
            {/* Global Undo Button */}
            {onUndo && (
              <button
                onClick={onUndo}
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition flex items-center gap-1"
                title="Undo all changes and revert to original scene"
              >
                <FaUndo className="text-xs" />
                Undo All
              </button>
            )}
            <button
              onClick={handleCancel}
              className="text-gray-500 hover:text-gray-700 transition"
            >
              <FaTimes className="text-lg" />
            </button>
          </div>
        </div>

        <div className="flex h-[calc(80vh-80px)]">
          {/* Left Panel - Current Scene Image */}
          <div className="flex-1 p-4">
            <div className="w-full h-full flex items-center justify-center">
              <div className="relative">
                <img
                  src={fullImageUrl}
                  alt="Scene"
                  className="max-w-full object-contain rounded-lg border-2 border-amber-200 shadow-lg"
                  style={{ 
                    aspectRatio: (sceneData && sceneData.tags && sceneData.tags.length > 1) ? '16/9' : '1/1',
                    maxHeight: (sceneData && sceneData.tags && sceneData.tags.length > 1) ? '60vh' : '50vh'
                  }}
                />
                
                {/* Scene Info Overlay */}
                {sceneData && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white p-2 rounded-b-lg">
                    <h3 className="font-bold text-sm">Scene {sceneData.sceneNumber || '1'}</h3>
                    <p className="text-xs opacity-90">
                      {sceneData.sceneDescription ? 
                        sceneData.sceneDescription.substring(0, 50) + '...' : 
                        'Scene description'
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Undo Button */}
            {editHistory.length > 0 && (
              <div className="mt-3 text-center">
                <button
                  onClick={handleUndo}
                  disabled={currentEditIndex <= -1}
                  className="bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white px-3 py-1 rounded text-sm transition flex items-center gap-1 mx-auto"
                  title="Undo to previous image"
                >
                  <FaUndo className="text-xs" />
                  Undo
                </button>
              </div>
            )}
          </div>

          {/* Right Panel - Simple Prompt */}
          <div className="w-80 bg-gray-50 p-4 flex flex-col">
            {/* Simple Instructions */}
            <div className="mb-4">
              <h3 className="text-sm font-medium text-amber-800 mb-2">Make your changes:</h3>
              <p className="text-xs text-gray-600 mb-2">
                Describe what you want to change about the scene
              </p>
            </div>

            {/* Prompt Input */}
            <div className="flex-1">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., Change the lighting to be more dramatic, add more trees in the background, make the colors warmer..."
                className="w-full h-32 p-3 border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none resize-none text-sm text-amber-800 bg-white"
                disabled={isLoading}
              />
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 mt-4">
              <button
                onClick={handleEditScene}
                disabled={isLoading || !prompt.trim()}
                className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Editing...</span>
                  </>
                ) : (
                  <>
                    <FaEdit />
                    <span>Edit Scene</span>
                  </>
                )}
              </button>

              <div className="flex space-x-2">
                <button
                  onClick={handleSave}
                  disabled={currentImageUrl === originalImageUrl}
                  className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <FaSave />
                  <span>Save</span>
                </button>
                <button
                  onClick={handleCancel}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Moderation Modal */}
      <ContentModerationModal
        isOpen={showContentModerationModal}
        onClose={() => setShowContentModerationModal(false)}
        message={contentModerationMessage}
        matchedKeywords={contentModerationKeywords}
      />
    </div>
  );
} 